import React, { useState } from 'react';

import { Button } from '@/components/ui/button';
import { ChevronDown, ChevronUp } from 'lucide-react';
import { Collapsible, CollapsibleContent } from '@/components/ui/collapsible';
import usePersistentOpen from '@/hooks/usePersistentOpen';
import { DetailsSectionHeader } from './DetailsSection';

import Lightbox from 'react-18-image-lightbox';

// The images bucket also holds videos and audio sent through the API.
const NON_IMAGE_EXTENSION = /\.(mp4|mov|mp3|m4a|amr)(\?|$)/i;

const Attachments = ({ messages, _activeConversation }) => {
	const [isLightboxOpen, setIsLightboxOpen] = useState(false);
	const [photoIndex, setPhotoIndex] = useState(0);
	const [isExpanded, setIsExpanded] = useState(false);
	const [isOpen, setIsOpen] = usePersistentOpen(
		'conversation-details:attachments',
	);

	const messageMedias = messages
		.filter(
			(message) =>
				message.media?.includes('image') &&
				!NON_IMAGE_EXTENSION.test(message.media),
		)
		.map((message) => {
			return message.media;
		})
		.reverse();

	const displayedMedias = isExpanded
		? messageMedias
		: messageMedias.slice(0, 4);

	if (messageMedias.length === 0) {
		return null;
	}

	return (
		<div key={_activeConversation}>
			{isLightboxOpen && (
				<Lightbox
					mainSrc={messageMedias[photoIndex]}
					nextSrc={
						messageMedias[(photoIndex + 1) % messageMedias.length]
					}
					prevSrc={
						messageMedias[
							(photoIndex + messageMedias.length - 1) %
								messageMedias.length
						]
					}
					onCloseRequest={() => setIsLightboxOpen(false)}
					reactModalStyle={{ zIndex: 99999 }}
					onMovePrevRequest={() =>
						setPhotoIndex(
							(photoIndex + messageMedias.length - 1) %
								messageMedias.length
						)
					}
					onMoveNextRequest={() =>
						setPhotoIndex(
							(photoIndex + messageMedias.length + 1) %
								messageMedias.length
						)
					}
				/>
			)}
			<div className="border-b border-border/60" key={_activeConversation}>
				<Collapsible open={isOpen} onOpenChange={setIsOpen}>
					<DetailsSectionHeader title="Images" />
					<CollapsibleContent>
						<div className="px-4 pb-3">
							<div
								className={`grid grid-cols-2 gap-2 ${
									isExpanded ? '' : 'max-h-[400px]'
								} overflow-hidden transition-all duration-300`}
							>
								{displayedMedias.length === 0 && (
									<div className="text-start text-sm text-gray-500">
										No images
									</div>
								)}
								{displayedMedias.map((media, index) => (
									<div
										key={index}
										role="button"
										tabIndex={0}
										aria-label={`Open image ${index + 1} of ${messageMedias.length}`}
										className="aspect-square relative cursor-pointer overflow-hidden rounded-lg focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
										onClick={() => {
											setPhotoIndex(index);
											setIsLightboxOpen(true);
										}}
										onKeyDown={(event) => {
											if (event.key === 'Enter' || event.key === ' ') {
												event.preventDefault();
												setPhotoIndex(index);
												setIsLightboxOpen(true);
											}
										}}
									>
										<img
											src={media}
											alt={`Attachment ${index + 1}`}
											className="object-cover w-full h-full hover:scale-105 transition-transform"
										/>
									</div>
								))}
							</div>
							{messageMedias.length > 4 && (
								<Button
									variant="outline"
									onClick={() => setIsExpanded(!isExpanded)}
									className="w-full mt-4 flex items-center justify-center gap-2 max-md:min-h-11"
								>
									{isExpanded ? (
										<>
											Show less{' '}
											<ChevronUp className="h-4 w-4" />
										</>
									) : (
										<>
											View all{' '}
											<ChevronDown className="h-4 w-4" />
										</>
									)}
								</Button>
							)}
						</div>
					</CollapsibleContent>
				</Collapsible>
			</div>
		</div>
	);
};

export default Attachments;
